import { FC, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { ViewBoxProps } from 'src/models/DialogBox.interface';
import { CustomerStatus } from 'src/models/enums/CustomerStatus.enum';
import { PurchaseOrderStatus } from 'src/models/enums/PurchaseOrderStatus.enum';
import { BankStatus } from 'src/models/enums/BankStatus.enum';
import Scrollbar from 'src/components/StyledComponents/StyledScrollbar';
import Status from 'src/components/Status';
import {
  Container,
  TitleInfo,
  TitleRow,
  TitleText,
  Subtitle,
  OrgName,
  ContactBox,
  ContactLabel,
  ContactInfo,
  IconBox,
  SuspendedReasonBox,
  SuspendedReasonText,
  ErrorIcon,
  FlexRow,
  InfoBlock,
  LabelText,
  InfoText,
  InfoTextMax200,
  InfoTextMax120,
  FlexRowGap,
  FlexColumnEnd,
  DeliveryAddressBox,
  DeliveryTitle,
  DeliveryIcon,
  DeliveryAddressText,
  ApprovalBox,
  ApprovalRow,
  ApprovalInfoColumn,
  ApprovalLabel,
  ApprovalText,
  ApprovalStatusBox,
  RejectionNoteBox,
  RejectionLabel,
  RejectionIcon,
  RejectionText,
  InfoTextMax160,
  StatusTitleBox,
  DialogContentBox,
  SubmitButtonBox,
  StyledCircularProgress,
  FlexColumnGapBox,
  FlexRowSpaceBetweenBox,
  FlexBoxTextLeft,
  RelativePositionBox,
} from 'src/components/DialogBox/POBox/POBox.styles';
import {
  CloseIcon,
  StyledDialog,
  StyledDialogContent,
  StyledSelect,
  SubmitButton,
  StatusIconBox,
  ErrorIconBox,
  TitleWithIconBox,
} from 'src/components/DialogBox/DialogBox.styles';
import {
  selectStatusPlaceholder,
  dialogTypeValues,
  dialogButtonLabels,
  dialogSpacingValues,
  approvalLabels,
} from 'src/utils/properties.utils';

const ViewBox = <
  TStatus extends PurchaseOrderStatus | CustomerStatus | BankStatus =
    | PurchaseOrderStatus
    | CustomerStatus
>(
  props: ViewBoxProps<TStatus> & { rightAligned?: boolean }
) => {
  const {
    height = '550px',
    width = '730px',
    type,
    open,
    handleClose,
    titleinfo,
    title,
    subtitle,
    orgName,
    contactNo,
    email,
    vendor,
    preparedBy,
    date,
    address,
    approvedBy,
    approvedDate,
    status: initialStatus,
    onStatusChange,
    statusOptions = [
      PurchaseOrderStatus.PENDING,
      PurchaseOrderStatus.APPROVED,
      PurchaseOrderStatus.REJECTED,
    ] as TStatus[],
    reason = '',
    children,
    onSubmit,
    smScreen,
    buttonLabel = dialogButtonLabels.SAVE,
    buttonWidth = '100px',
    buttonDisabled,
    hideButton = false,
    rightAligned = false,
  } = props;

  const [status, setStatus] = useState<TStatus>(
    initialStatus || (PurchaseOrderStatus.NEW as TStatus)
  );
  const [showReasonField, setShowReasonField] = useState<boolean>(
    initialStatus === PurchaseOrderStatus.REJECTED
  );

  const dialogHeight =
    status === CustomerStatus.SUSPENDED && reason
      ? 'auto'
      : showReasonField
      ? '650px'
      : height;

  useEffect(() => {
    if (open) {
      setStatus((initialStatus || PurchaseOrderStatus.NEW) as TStatus);
      setShowReasonField(initialStatus === PurchaseOrderStatus.REJECTED);
    }
  }, [open, initialStatus]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value as TStatus;
    setStatus(newStatus);
    setShowReasonField(newStatus === PurchaseOrderStatus.REJECTED);
    onStatusChange?.(newStatus);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      width={width}
      height={dialogHeight}
      rightAligned={rightAligned}
    >
      {type === dialogTypeValues.CUSTOMER && (
        <RelativePositionBox>
          <Container smScreen={smScreen}>
            <TitleInfo>{titleinfo}</TitleInfo>

            <TitleRow>
              <TitleText smScreen={smScreen}>{title}</TitleText>
              <Status status={status || ''} />
            </TitleRow>

            <Subtitle>{subtitle}</Subtitle>
            <OrgName>{orgName}</OrgName>

            <ContactBox>
              <ContactLabel>Contact</ContactLabel>

              <ContactInfo>
                <IconBox component="i" className="bx bx-phone-call" />
                {contactNo}
              </ContactInfo>

              {email && (
                <ContactInfo>
                  <IconBox component="i" className="bx bx-envelope" />
                  {email}
                </ContactInfo>
              )}
            </ContactBox>

            {status === CustomerStatus.SUSPENDED && (
              <SuspendedReasonBox>
                <SuspendedReasonText>
                  <ErrorIcon component="i" className="bx bx-error-circle" />
                  {reason}{' '}
                </SuspendedReasonText>
              </SuspendedReasonBox>
            )}
          </Container>

          <CloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleClose}
          />
        </RelativePositionBox>
      )}
      {type === dialogTypeValues.PURCHASE && (
        <RelativePositionBox>
          <Box
            ml={
              rightAligned
                ? 0
                : smScreen
                ? 0
                : dialogSpacingValues.PURCHASE_SECTION_MARGIN_LEFT
            }
            mr={rightAligned ? 0 : 0}
            mt={dialogSpacingValues.PURCHASE_SECTION_MARGIN_TOP}
            mb={dialogSpacingValues.PURCHASE_SECTION_MARGIN_BOTTOM}
            px={rightAligned ? 4 : 0}
          >
            <TitleRow>
              <TitleText smScreen={smScreen}>{title}</TitleText>
            </TitleRow>

            <FlexRow gap={0} fullWidth={rightAligned}>
              <Box flex={1}>
                <InfoBlock>
                  <LabelText>Vendor</LabelText>
                  <InfoTextMax200>{vendor}</InfoTextMax200>
                </InfoBlock>

                <FlexRowGap>
                  <Box flex={1}>
                    <LabelText>Prepared By</LabelText>
                    <InfoTextMax120>{preparedBy}</InfoTextMax120>
                  </Box>

                  <FlexColumnEnd>
                    <LabelText textAlign="right">Date</LabelText>
                    <InfoText textAlign="right">{date}</InfoText>
                  </FlexColumnEnd>
                </FlexRowGap>
              </Box>

              <DeliveryAddressBox>
                <DeliveryTitle>
                  <DeliveryIcon component="i" className="bx bx-buildings 1" />
                  Delivery Address
                </DeliveryTitle>
                <DeliveryAddressText>{address}</DeliveryAddressText>
              </DeliveryAddressBox>
            </FlexRow>

            <ApprovalBox status={status || ''} fullWidth={rightAligned}>
              <ApprovalRow>
                <ApprovalInfoColumn>
                  <ApprovalLabel>
                    {status === PurchaseOrderStatus.APPROVED
                      ? approvalLabels.APPROVED_BY
                      : status === PurchaseOrderStatus.REJECTED
                      ? approvalLabels.REJECTED_BY
                      : approvalLabels.APPROVED_BY}
                    :
                  </ApprovalLabel>
                  <ApprovalText>{approvedBy || '-'}</ApprovalText>
                </ApprovalInfoColumn>

                <ApprovalInfoColumn>
                  <ApprovalLabel>
                    {status === PurchaseOrderStatus.APPROVED
                      ? approvalLabels.APPROVED_DATE
                      : status === PurchaseOrderStatus.REJECTED
                      ? approvalLabels.REJECTED_DATE
                      : approvalLabels.APPROVED_DATE}
                    :
                  </ApprovalLabel>
                  <ApprovalText>{approvedDate || '-'}</ApprovalText>
                </ApprovalInfoColumn>

                <ApprovalStatusBox>
                  <Status status={status || ''} />
                </ApprovalStatusBox>
              </ApprovalRow>

              {status === PurchaseOrderStatus.REJECTED && reason && (
                <RejectionNoteBox>
                  <RejectionLabel>
                    <RejectionIcon
                      component="i"
                      className="bx bx-error-circle 1"
                    />
                    Rejection Note:
                  </RejectionLabel>
                  <RejectionText>{reason}</RejectionText>
                </RejectionNoteBox>
              )}
            </ApprovalBox>
          </Box>

          <CloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleClose}
          />
        </RelativePositionBox>
      )}
      {type === dialogTypeValues.STATUS && (
        <RelativePositionBox>
          <Container
            smScreen={smScreen}
            sx={{
              marginTop: dialogSpacingValues.STATUS_SECTION_MARGIN_TOP,
              marginBottom: dialogSpacingValues.STATUS_SECTION_MARGIN_BOTTOM,
            }}
          >
            <TitleRow>
              <TitleWithIconBox>
                <ErrorIconBox component="i" className="bx bx-error-circle 1" />
                <TitleText
                  smScreen={false}
                  style={{
                    color: 'var(--primary-navy)',
                    maxWidth: '100%',
                    overflow: 'visible',
                    whiteSpace: 'normal',
                  }}
                  mb={dialogSpacingValues.STATUS_TITLE_MARGIN_BOTTOM}
                >
                  {title}
                </TitleText>
              </TitleWithIconBox>
            </TitleRow>

            <FlexColumnGapBox gap={0}>
              <FlexRowSpaceBetweenBox
                width={dialogSpacingValues.STATUS_CONTAINER_WIDTH}
              >
                <Box>
                  <TitleInfo>Vendor</TitleInfo>
                  <InfoTextMax200>{vendor}</InfoTextMax200>
                </Box>

                {approvedDate && (
                  <FlexBoxTextLeft>
                    <TitleInfo>Date Approved</TitleInfo>
                    <TitleInfo style={{ color: 'var(--primary-navy)' }}>
                      {approvedDate || date}
                    </TitleInfo>
                  </FlexBoxTextLeft>
                )}

                <FlexBoxTextLeft>
                  <StatusTitleBox>
                    <TitleInfo
                      mb={dialogSpacingValues.STATUS_TITLE_MARGIN_BOTTOM}
                    >
                      Status
                    </TitleInfo>
                    <StyledSelect
                      id="status"
                      name="status"
                      value={status || ''}
                      onChange={handleStatusChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Box sx={{ color: 'var(--instructions)' }}>
                              {selectStatusPlaceholder}
                            </Box>
                          );
                        }
                        return <Status status={selected} />;
                      }}
                      IconComponent={() => (
                        <StatusIconBox
                          component="i"
                          className="bx bxs-chevron-down"
                        />
                      )}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{
                            fontFamily: 'Outfit',
                            fontSize: '16px',
                            '&.Mui-selected': {
                              backgroundColor: 'var(--inactive-btn)',
                              color: 'var(--primary-blue)',
                            },
                          }}
                        >
                          <Status status={option} />
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </StatusTitleBox>
                </FlexBoxTextLeft>
              </FlexRowSpaceBetweenBox>

              <Box>
                <TitleInfo>Prepared By</TitleInfo>
                <InfoTextMax160>{preparedBy}</InfoTextMax160>
              </Box>
            </FlexColumnGapBox>
          </Container>

          <CloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleClose}
          />
        </RelativePositionBox>
      )}

      <StyledDialogContent rightAligned={rightAligned}>
        <form onSubmit={onSubmit}>
          <DialogContentBox
            ml={
              rightAligned
                ? 0
                : type === dialogTypeValues.STATUS
                ? dialogSpacingValues.STATUS_SECTION_CONTENT_MARGIN_LEFT
                : smScreen
                ? 0
                : 5
            }
            mr={
              rightAligned
                ? 0
                : type === dialogTypeValues.STATUS
                ? dialogSpacingValues.STATUS_SECTION_CONTENT_MARGIN_LEFT
                : 0
            }
            mt={
              type === dialogTypeValues.STATUS
                ? dialogSpacingValues.STATUS_SECTION_CONTENT_MARGIN_TOP
                : 0
            }
            display={
              rightAligned || type === dialogTypeValues.STATUS
                ? 'block'
                : 'grid'
            }
            gridTemplateColumns={
              rightAligned || type === dialogTypeValues.STATUS
                ? undefined
                : 'repeat(2, 1fr)'
            }
            gap={2}
            px={rightAligned ? 4 : 0}
          >
            {children}
          </DialogContentBox>
          {!hideButton && (
            <SubmitButtonBox
              ml={
                type === dialogTypeValues.PURCHASE
                  ? dialogSpacingValues.MARGIN_LEFT_PURCHASE
                  : dialogSpacingValues.MARGIN_LEFT_STATUS
              }
              mt={dialogSpacingValues.MARGIN_TOP_BUTTON}
              mb={dialogSpacingValues.MARGIN_BOTTOM_BUTTON}
            >
              <SubmitButton
                className="btn-primary"
                type="submit"
                disabled={buttonDisabled}
                buttonWidth={buttonWidth}
              >
                {buttonDisabled ? (
                  <StyledCircularProgress
                    size={dialogSpacingValues.CIRCULAR_PROGRESS_SIZE}
                  />
                ) : (
                  buttonLabel
                )}
              </SubmitButton>
            </SubmitButtonBox>
          )}
        </form>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default ViewBox;
